import { useEffect, useRef } from 'react';

/**
 * Hook that executes a callback only once when the specified condition(s) are met.
 * Useful for tracking events, initialization logic, or any one-time side effects.
 *
 * The callback is stored in a ref to prevent re-execution when the callback reference changes
 * (e.g., when it captures changing dependencies like analytics track functions).
 *
 * @param callback - The function to execute once
 * @param condition - Optional condition that must be truthy for the callback to execute.
 *                   If not provided, executes once on mount.
 *
 * @example
 * ```tsx
 * // Execute once on mount
 * useEffectOnce(() => {
 *   console.log('Component mounted');
 * });
 *
 * // Execute once when data is loaded (won't fire again if data changes)
 * useEffectOnce(() => {
 *   track('DataLoaded', { count: data.length });
 * }, data && !isLoading);
 * ```
 */
export function useEffectOnce(callback: () => void, condition?: unknown): void {
  const hasExecutedRef = useRef(false);
  const callbackRef = useRef(callback);

  // Always store the latest callback in ref
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    // If already executed, don't run again
    if (hasExecutedRef.current) {
      return;
    }

    // Check condition - must be truthy (or undefined for mount-only execution)
    const shouldExecute = condition === undefined || Boolean(condition);

    if (shouldExecute) {
      hasExecutedRef.current = true;
      // Use the latest callback from ref
      callbackRef.current();
    }
  }, [condition]);
}
